/**
 * Shrink a phone photo before upload: respect the camera's EXIF rotation,
 * cap the long edge (default 2400px — plenty for handwriting), re-encode as
 * JPEG, and step quality/size down until it fits under `maxBytes`. The API
 * takes images up to 5 MB base64-encoded (~3.7 MB raw), and a 12 MP phone
 * photo is often bigger than that.
 */

export interface ShrunkImage { blob: Blob; width: number; height: number; type: string }

/** Target size keeping the aspect ratio, long edge ≤ maxEdge (never upscales). */
export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (!long || long <= maxEdge) return { width, height };
  const k = maxEdge / long;
  return { width: Math.round(width * k), height: Math.round(height * k) };
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      /* fall back to <img> (older WebViews, some formats) */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("This photo couldn't be opened. Try a JPG or PNG."));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

const toBlob = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not process the photo.'))), 'image/jpeg', quality);
  });

export async function shrinkImage(file: Blob, { maxEdge = 2400, maxBytes = 3_600_000 } = {}): Promise<ShrunkImage> {
  const img = await decode(file);
  try {
    const attempts: [number, number][] = [[maxEdge, 0.86], [maxEdge, 0.74], [Math.round(maxEdge * 0.8), 0.72], [Math.round(maxEdge * 0.65), 0.7]];
    let last: ShrunkImage | null = null;
    for (const [edge, quality] of attempts) {
      const { width, height } = fitWithin(img.width, img.height, edge);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not process the photo.');
      ctx.drawImage(img.source, 0, 0, width, height);
      const blob = await toBlob(canvas, quality);
      last = { blob, width, height, type: 'image/jpeg' };
      if (blob.size <= maxBytes) return last;
    }
    return last!;
  } finally {
    img.close();
  }
}
